/* Roadhouse Merkliste: remembered pieces, stored in the visitor's browser (no app, no account).
 *
 * Buttons: any element with [data-rh-wish] and data-handle/-title/-url/-image/-price/-vendor
 * toggles the piece. [data-rh-wish-label] inside shows "Merken"/"Gemerkt".
 * Texts in the shop language come from data-t-add/-remove/-on/-off/-added/-removed on the button
 * (snippets/rh-wish-button.liquid); the German literals below are only the fallback.
 * Counter: [data-rh-wish-count] shows the number of pieces (hidden at 0).
 * The page section rh-wishlist reads the list through window.RhWishlist.
 *
 * The old wishlist (OnHOW code) used two different storage keys; both are merged once. */
(() => {
  'use strict';
  const KEY = 'rh-wishlist';
  const OLD_KEYS = ['onhow-wishlist', 'onhow_wishlist'];

  const handleFromUrl = (url) => {
    const m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };
  const parse = (raw) => { try { const v = JSON.parse(raw || 'null'); return Array.isArray(v) ? v : null; } catch { return null; } };
  const normalize = (it) => {
    const handle = it.handle || handleFromUrl(it.url);
    if (!handle) return null;
    return { handle, title: String(it.title || ''), url: it.url || `/products/${handle}`, image: it.image || '', price: String(it.price || ''), vendor: String(it.vendor || ''), added: it.added || Date.now() };
  };

  function read() {
    let list = null;
    try { list = parse(localStorage.getItem(KEY)); } catch { return []; }
    if (list) return list;
    // first visit with the new code: take over what the old wishlist stored
    const merged = [];
    try {
      OLD_KEYS.forEach((k) => (parse(localStorage.getItem(k)) || []).forEach((it) => {
        const n = normalize(it);
        if (n && !merged.some((m) => m.handle === n.handle)) merged.push(n);
      }));
      localStorage.setItem(KEY, JSON.stringify(merged));
    } catch { /* storage blocked: start empty */ }
    return merged;
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* storage blocked */ }
    render();
    document.dispatchEvent(new CustomEvent('rh:wishlist', { detail: { list } }));
  }
  const has = (handle) => read().some((it) => it.handle === handle);
  function toggle(data) {
    const list = read();
    const i = list.findIndex((it) => it.handle === data.handle);
    if (i >= 0) list.splice(i, 1);
    else { const n = normalize(data); if (n) list.unshift(n); }
    write(list);
    return i < 0;
  }
  const remove = (handle) => write(read().filter((it) => it.handle !== handle));

  function render() {
    const list = read();
    const set = new Set(list.map((it) => it.handle));
    document.querySelectorAll('[data-rh-wish]').forEach((btn) => {
      const on = set.has(btn.dataset.handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const title = btn.dataset.title || '';
      const d = btn.dataset;
      btn.setAttribute('aria-label', on ? (d.tRemove || `${title} von der Merkliste nehmen`) : (d.tAdd || `${title} merken`));
      const label = btn.querySelector('[data-rh-wish-label]');
      if (label) label.textContent = on ? (d.tOn || 'Gemerkt') : (d.tOff || 'Merken');
    });
    document.querySelectorAll('[data-rh-wish-count]').forEach((el) => {
      el.textContent = String(list.length);
      el.hidden = list.length === 0;
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rh-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const added = toggle({ ...btn.dataset });
    btn.classList.remove('is-pop');
    void btn.offsetWidth; // restart the short confirmation
    if (added) btn.classList.add('is-pop');
    const live = document.getElementById('rh-wish-live');
    const d = btn.dataset;
    if (live) live.textContent = added ? (d.tAdded || `${d.title} gemerkt.`) : (d.tRemoved || `${d.title} von der Merkliste genommen.`);
  });

  // other tabs, and product grids that are re-rendered by filters or search
  window.addEventListener('storage', (e) => { if (e.key === KEY) render(); });
  let t = 0;
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-rh-wish]') || n.querySelector?.('[data-rh-wish]'))))) {
      clearTimeout(t); t = setTimeout(render, 30);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  const live = document.createElement('div');
  live.id = 'rh-wish-live';
  live.setAttribute('aria-live', 'polite');
  live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
  const boot = () => { if (!document.getElementById('rh-wish-live')) document.body.appendChild(live); render(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.RhWishlist = { read, write, has, toggle, remove, render };
})();

/* Roadhouse: share the Merkliste or the cart (27.10.2026). No account, no app: the link carries the pieces.
 *   Merkliste: /pages/<merkliste>?liste=handle,handle – the page shows them with "Alle in meine Merkliste"
 *              (sections/rh-wishlist.liquid). The visitor's own list stays untouched until they choose.
 *   Warenkorb: /cart?geteilt=variant:qty,… – on opening, the pieces are added to the visitor's cart (existing items
 *              stay), sold ones are skipped and named in a short note.
 * Phones open the share sheet; elsewhere the link is copied, and shown to select by hand if copying is blocked.
 * Buttons: [data-rh-share-cart] (cart page and drawer, texts in data-t-*). */
(() => {
  'use strict';
  const NOTE_KEY = 'rh-share-note';
  const MAX_LINES = 30;

  async function shareLink(url, title, status) {
    const say = (text, link) => {
      if (!status) return;
      status.hidden = false;
      status.textContent = text;
      if (link) {
        const field = document.createElement('input');
        field.className = 'rh-share__field';
        field.readOnly = true;
        field.value = link;
        field.setAttribute('aria-label', title);
        status.appendChild(field);
        field.focus();
        field.select();
      }
    };
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try { await navigator.share({ title, url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      say(status && status.dataset.tCopied);
    } catch {
      say(status && status.dataset.tManual, url);
    }
  }

  // cart: build the link from the live cart, because the drawer changes without a reload
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rh-share-cart]');
    if (!btn) return;
    e.preventDefault();
    const status = btn.parentElement.querySelector('[data-rh-share-status]');
    if (status) { status.hidden = true; status.textContent = ''; }
    const cartUrl = btn.dataset.cartUrl || '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const lines = (cart.items || []).slice(0, MAX_LINES).map((it) => `${it.variant_id}:${it.quantity}`);
        if (!lines.length) return;
        const url = `${window.location.origin}${cartUrl}?geteilt=${lines.join(',')}`;
        return shareLink(url, btn.dataset.tTitle || document.title, status);
      })
      .catch(() => { if (status) { status.hidden = false; status.textContent = btn.dataset.tFailed || ''; } });
  });

  // opening a shared cart: add the pieces, then show the cart without the parameter
  function receiveCart() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('geteilt');
    if (raw === null || !/\/cart\/?$/.test(window.location.pathname)) return;
    const wanted = raw.split(',').map((s) => s.trim()).filter((s) => /^\d{1,20}:\d{1,2}$/.test(s)).slice(0, MAX_LINES)
      .map((s) => { const [id, qty] = s.split(':'); return { id: Number(id), quantity: Math.max(1, Number(qty)) }; });
    const cartUrl = window.location.pathname.replace(/\/$/, '');
    const done = (note) => {
      try { sessionStorage.setItem(NOTE_KEY, JSON.stringify(note)); } catch { /* storage blocked: no note */ }
      params.delete('geteilt');
      const rest = params.toString();
      window.location.replace(cartUrl + (rest ? `?${rest}` : ''));
    };
    if (!wanted.length) { done({ added: 0, missing: 0 }); return; }
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(async (cart) => {
        let added = 0;
        let missing = 0;
        for (const line of wanted) {
          const inCart = (cart.items || []).filter((it) => it.variant_id === line.id).reduce((n, it) => n + it.quantity, 0);
          const quantity = line.quantity - inCart;
          if (quantity <= 0) { added += 1; continue; }
          // one by one: a sold piece must not stop the others
          const r = await fetch(`${cartUrl}/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: line.id, quantity }] }),
          }).catch(() => null);
          if (r && r.ok) added += 1; else missing += 1;
        }
        done({ added, missing });
      })
      .catch(() => done({ added: 0, missing: wanted.length }));
  }

  function showNote() {
    let note = null;
    try { note = JSON.parse(sessionStorage.getItem(NOTE_KEY) || 'null'); sessionStorage.removeItem(NOTE_KEY); } catch { return; }
    const box = document.querySelector('[data-rh-share-note]');
    if (!note || !box) return;
    const d = box.dataset;
    const parts = [];
    if (note.added === 1) parts.push(d.tAddedOne);
    else if (note.added > 1) parts.push(String(d.tAddedOther).replace('%N%', note.added));
    if (note.missing > 0) parts.push(note.added ? (note.missing === 1 ? d.tMissingOne : String(d.tMissing).replace('%N%', note.missing)) : d.tNone);
    if (!parts.length) parts.push(d.tNone);
    box.textContent = parts.join(' ');
    box.hidden = false;
  }

  const boot = () => { receiveCart(); showNote(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.RhShare = { shareLink };
})();
